import type { ComponentIR } from "@aaac/contracts";
import type { RuntimeBinding } from "./component-binding.js";
import type { ResolvedComponent, ResolvedToolchain } from "./lib/bound-resolver.js";
import type { GuardrailRuleData } from "./lib/guardrail-hooks.js";
import {
  createLibraryToolRegistry,
  type LibraryToolDefinition,
  type LibraryToolRegistry,
} from "./lib/library-tool-binding.js";

export interface ComponentScope {
  componentId: string;
  componentIR: ComponentIR;
  binding: RuntimeBinding;
  toolsByAgent: Map<string, string[]>;
  guardrailsByAgent: Map<string, unknown[]>;
  libraryTools?: LibraryToolRegistry;
  toolchainsByAgent?: Map<string, ResolvedToolchain[]>;
}

export interface CreateComponentScopeOptions {
  resolvedComponent?: ResolvedComponent;
  libraryToolDefs?: LibraryToolDefinition[];
}

interface DslToolEntry {
  component_contract?: string;
  command?: string;
  invokable_by?: string[];
}

interface DslAgentEntry {
  can_execute_tools?: string[];
}

function buildToolsByAgent(
  componentIR: ComponentIR,
  resolved?: ResolvedComponent,
): Map<string, string[]> {
  const operationNames = new Set(Object.keys(componentIR.operations));
  const toolsByAgent = new Map<string, string[]>();

  if (!resolved) {
    return toolsByAgent;
  }

  const dsl = resolved.dsl;
  const tools = (dsl.tools ?? {}) as Record<string, DslToolEntry>;

  for (const tool of Object.values(tools)) {
    const command = tool.command;
    if (!command || !operationNames.has(command)) {
      continue;
    }

    for (const agentId of tool.invokable_by ?? []) {
      appendToolForAgent(toolsByAgent, agentId, command);
    }
  }

  const agents = (dsl.agents ?? {}) as Record<string, DslAgentEntry>;
  for (const [agentId, agent] of Object.entries(agents)) {
    for (const toolName of agent.can_execute_tools ?? []) {
      if (operationNames.has(toolName)) {
        appendToolForAgent(toolsByAgent, agentId, toolName);
      }
    }
  }

  if (resolved.toolchainsByAgent) {
    for (const [agentId, toolchains] of resolved.toolchainsByAgent) {
      for (const tc of toolchains) {
        if (tc.operations) {
          for (const op of tc.operations) {
            if (operationNames.has(op)) {
              appendToolForAgent(toolsByAgent, agentId, op);
            }
          }
        }
      }
    }
  }

  return toolsByAgent;
}

function appendToolForAgent(
  toolsByAgent: Map<string, string[]>,
  agentId: string,
  operationName: string,
): void {
  const existing = toolsByAgent.get(agentId) ?? [];
  if (existing.includes(operationName)) {
    return;
  }
  toolsByAgent.set(agentId, [...existing, operationName]);
}

function buildGuardrailsByAgent(
  resolved?: ResolvedComponent,
): Map<string, unknown[]> {
  const guardrailsByAgent = new Map<string, unknown[]>();

  if (!resolved) {
    return guardrailsByAgent;
  }

  for (const [agentId, rules] of resolved.guardrails.perAgent) {
    guardrailsByAgent.set(agentId, [rules satisfies GuardrailRuleData]);
  }

  return guardrailsByAgent;
}

function buildLibraryTools(
  defs?: LibraryToolDefinition[],
): LibraryToolRegistry | undefined {
  if (!defs || defs.length === 0) {
    return undefined;
  }

  const registry = createLibraryToolRegistry();
  for (const def of defs) {
    registry.register(def);
  }
  return registry;
}

export function createComponentScope(
  componentIR: ComponentIR,
  binding: RuntimeBinding,
  options?: CreateComponentScopeOptions,
): ComponentScope {
  const resolved = options?.resolvedComponent;

  return {
    componentId: componentIR.info.id,
    componentIR,
    binding,
    toolsByAgent: buildToolsByAgent(componentIR, resolved),
    guardrailsByAgent: buildGuardrailsByAgent(resolved),
    libraryTools: buildLibraryTools(options?.libraryToolDefs),
    ...(resolved?.toolchainsByAgent
      ? { toolchainsByAgent: resolved.toolchainsByAgent }
      : {}),
  };
}
